import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { NotificationsComponent } from './shared/notifications/notifications.component';
import { Location } from '@angular/common';

import { Title } from '@angular/platform-browser';
import { CoreNavigationComponent } from './core/core-navigation/core-navigation.component';
import { TutorialWidgetComponent } from './tutorial/tutorial-widget/tutorial-widget.component';
import { MediaLibraryModalComponent } from './media/media-library-modal/media-library-modal.component';
import { CoreDialogModalComponent } from './core/core-dialog-modal/core-dialog-modal.component';
import { CoreRequiredWorkModalComponent } from './core/core-required-work-modal/core-required-work-modal.component';
import { Clipboard } from '@angular/cdk/clipboard';
import * as countryLookup from 'country-code-lookup';

declare const window: any;
declare const Mangler: any;
declare const $: any;

@Injectable()
export class AppService {

	private themes = {
		standard: {
			id: 'standard',
			assetSuffix: '',
			styleSheet: null
		},
		pro: {
			id: 'pro',
			assetSuffix: '-pro',
			styleSheet: null
		}
	};

	activeTheme = this.themes.standard;

	notifications: NotificationsComponent = null;
	navigation: CoreNavigationComponent = null;
	tutorial: TutorialWidgetComponent = null;
	dialog: CoreDialogModalComponent = null;

	// The "have you completed…?" checkpoint (CRM-143). Global for the same reason the dialog is:
	// the deal workspace and three quote screens all raise it, and one of those is itself a modal.
	requiredWork: CoreRequiredWorkModalComponent = null;

	mediaLibrary: MediaLibraryModalComponent = null;
	onMediaLibrarySelect: EventEmitter<any> = new EventEmitter();

	countries = [];

	private _org = null;
	orgInfo = null;
	/**
	 * The organisation's own details have arrived (or changed).
	 *
	 * `orgInfo` is a plain field set asynchronously after the shell boots, so anything that has to
	 * wait for it — whether a module is switched on, which permissions the role has — has no way
	 * to know when to look. Components that render before it lands would otherwise decide "no" and
	 * never revisit it.
	 */
	public onOrgInfoChange = new EventEmitter<any>();

	/**
	 * Something that Needs Your Attention shows has just been written.
	 *
	 * Emitted by whatever did the writing — a follow-up saved on a deal, one completed or
	 * reopened, required work resolved, a stage move that raises or supersedes some. The widget
	 * lives outside every screen that can change its list, and had no way to hear about any of
	 * it: until this, a follow-up added on a deal sat unshown until the next navigation.
	 *
	 * A signal rather than a payload. The widget's list is scoped, filtered and sorted
	 * server-side, so working out where a new row belongs client-side would be a second copy of
	 * rules that already exist in one place — it re-reads instead. Nothing outside the widget
	 * needs to know that, so callers just say what happened.
	 *
	 * This covers only what this browser did. Automations run server-side and a colleague works
	 * in their own session, so the widget also polls; the two answer different halves of the same
	 * question and neither replaces the other.
	 *
	 * A write made from outside the deal's own screen names the deal, so that screen can re-read
	 * too: the required-work dialog sits over the deal page, and without the id the page went on
	 * listing work the dialog had just marked done. A screen that made the write itself emits
	 * nothing, because it has already re-read.
	 */
	public onAttentionChange = new EventEmitter<number | void>();

	userImage = '';
	alertCount = 0;
	translatedVATTerm = 'VAT';

	calendarDateFormat = 'dd/mm/yy';
	calendarHourFormat = '24';

	shouldSkipDeviceCheck = false;

	path = '';

	introductionRouteId;
	blockQuotaLimitUsers = false;
	search = {
		catalogueSubscriptions: '',
		categoryList: '',
		customerList: '',
		entityList: '',
		labourList: '',
		productList: '',
		productCategoryList: '',
		productCounterList: '',
		quoteList: '',
		projectList: '',
		projectFinancialList: '',
		invoiceList: '',
		subscriptionList: '',
		catalogueList: '',
		poList: '',
		introPageList: '',
		pickList: '',
		transactionList: '',
		taskList: '',
		workOrderList: '',
		reportsList: '',
		tutorialList: '',
		requestList: '',
		approvalList: ''
	};

	tabs = {
		catalogue: []
	};

	sort = {
		quoteList: '!modified_datetime',
		projectList: '!modified_datetime',
		invoiceList: '!bill_date',
		poList: '!order_date',
		introPageList: '!modified_datetime',
		workOrderList: '!work_order_no',
		taskList: '!id',
		billableTaskList: '!id',
		reportsList: '!id',
		requestList: '!request_no',
		approvalList: '!request_no',
		expenseList: '!id',
		requestCartList: '!id',
		projectFinanceList: 'last_delivery_date'
	};

	filter = {
		showArchivedEntities: false
	};

	tutorialData = {
		catalogueId: 28
	};

	// Available toolbar items: selectAll, undo, redo, bold, italic, blockQuote, ckfinder, imageTextAlternative, imageUpload, heading, imageStyle:full, imageStyle:side, indent, outdent, link, numberedList, bulletedList, mediaEmbed, insertTable, tableColumn, tableRow, mergeTableCells

	tinyContent = [
		'/print/css/bootstrap.min.css',
		'/print/css/wequote-icons.css?rev=49',
		'/print/css/styles.css?rev=6',
		'/print/print_quote_template.css?rev=3',
		'https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,400;0,700;1,400;1,700&display=swap',
		'/print/css/tinymce.css'
	];

	tinyContentFullWidth = [
		'/print/css/bootstrap.min.css',
		'/print/css/wequote-icons.css?rev=49',
		'/print/css/styles.css?rev=6',
		'/print/print_quote_template.css?rev=3',
		'https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,400;0,700;1,400;1,700&display=swap',
		'/print/css/tinymce-full-width.css'
	];

	tinyInit = {
		plugins: [
			'advlist autolink lists link image charmap print preview anchor',
			'searchreplace visualblocks code fullscreen',
			'insertdatetime media table paste code help wordcount',
			'autoresize pagebreak'
		],
		toolbar: 'undo redo | formatselect | bold italic underline | forecolor backcolor removeformat | link image table pagebreak | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | code',
		base_url: '/tinymce',
		suffix: '.min',

		menubar: false, // True to show menu above editor, or text list of menus enabled
		autoresize_bottom_margin: 0,
		min_height: 200,
		max_height: 500,
		statusbar: false,
		placeholder: 'Content',
		toolbar_sticky: true,

		content_css: this.tinyContent,

		image_advtab: true,
		image_description: false,

		relative_urls: false,
		convert_urls: false,
		remove_script_host: false,

		browser_spellcheck: true,

		pagebreak_separator: '<div style="page-break-before: always;" class="page-break-section"></div>',
		pagebreak_split_block: true,

		extended_valid_elements: 'i[id|class|style],em,iframe[src|frameborder|style|scrolling|class|width|height|name|align|allow]',

		file_picker_callback: (callback, value, meta) => {
			if (meta.filetype === 'image') {
				if (this.mediaLibrary) {
					this.zone.run(() => {
						$('.tox-dialog-wrap').hide();
						$('.tox-tinymce-aux').hide();

						const sub = this.onMediaLibrarySelect.subscribe(data => {
							sub.unsubscribe();
							$('.tox-dialog-wrap').show();
							$('.tox-tinymce-aux').show();
							if (data) callback(data.url);
						});

						this.mediaLibrary.open({});
					});
				}
			}
		}
	};

	quoteHeaderUpdateTrigger = 0;
	askAutoApprove = false;

	selectedCalendarUsers = [];

	// Marketplace last picked in a marketplace panel, offered first the next time one opens. Kept when
	// switching account, as staff often onboard several accounts from the same market in a row.
	lastMarketplaceId = null;

	// Account status helpers. organisation.status is authoritative - don't re-derive
	// account state from dates or billing flags in components.
	get isTrialAccount() { return this.orgInfo?.status === 'trial' || this.orgInfo?.status === 'trial_expired'; }
	get isSnaggAccount() { return this.orgInfo?.status === 'snagg_only'; }

	// Statuses that leave the user with no usable account until they act. Both land on
	// the same screen, where they can start or restart a subscription.
	get isLockedOutAccount() { return this.orgInfo?.status === 'trial_expired' || this.orgInfo?.status === 'cancelled'; }

	// Marketplace accounts staff can copy to and from, never the account being worked in. Empty for everyone else.
	get marketplaces(): any[] { return this.orgInfo?.marketplaces || []; }
	get defaultMarketplace() { return this.marketplaces.find(m => m.id === this.lastMarketplaceId) || this.marketplaces[0] || null; }

	/**
	 * Whether this user should see the CRM. Four screens ask, so the rule lives here.
	 *
	 * allow_crm is the organisation's feature flag and is required outright. access_crm is a role
	 * permission, and root bypasses permissions server-side — Role::check returns true for root —
	 * so it has to bypass here too, or a root user gets the module from the server-generated nav
	 * while every CRM panel on a non-CRM screen silently hides. That is the state on staging today:
	 * allow_crm is on and no role grants access_crm yet (CRM-109).
	 *
	 * Same shape as the existing `role.is_root || allow_export_quote_list` checks on the quote
	 * screens.
	 */
	get crmEnabled() {
		if (!this.orgInfo?.allow_crm) return false;
		return !!this.orgInfo?.role?.access_crm || !!this.orgInfo?.role?.is_root;
	}

	get dashboardEnabled() {
		return !!this.orgInfo?.role?.access_dashboard || !!this.orgInfo?.role?.is_root;
	}

	/**
	 * The Needs Your Attention switch in Customise your workspace, saved per user per organisation
	 * on organisation_users.widget_prefs and carried on org info like every other preference here.
	 *
	 * Absent means on: the widget is the point of the feature, so somebody who has never opened
	 * the popup should see it rather than have to go and find the switch.
	 */
	get attentionWidgetEnabled() {
		return this.orgInfo?.widget_prefs?.attention !== false;
	}

	get org() { return this._org; }
	set org(value) {
		if (this._org !== value) {
			this.tabs.catalogue = [];
			this.selectedCalendarUsers = [];
			this._org = value;
			if (!value) {
				this.orgInfo = null;
				this.refreshTitle();
			}
			this.syncChatIdentity();
		}
	}

	get portalBaseUrl() {
		const alias = this.orgInfo?.portal_alias || this._org;
		let base = this.orgInfo?.portal_url;
		// if (!base) {
		// 	const host = window.location.hostname;
		// 	base = 'weproject.cloud';
		// 	if (host.includes('staging')) base = 'weprojects.staging';
		// 	else if (host.endsWith('.test')) base = 'weprojects.test';
		// }
		return 'https://' + alias + '.' + base;
	}

	portalProjectUrl(projectId) {
		return this.portalBaseUrl + '/project/' + projectId;
	}

	// Same host as portalBaseUrl, but on the scheme the browser is actually using. Links we hand to
	// a customer are always https, so portalBaseUrl hardcodes it; a preview we frame ourselves has to
	// match the current page or dev (plain http on .test) would be blocked as mixed content.
	get portalOrigin() {
		const alias = this.orgInfo?.portal_alias || this._org;
		return window.location.protocol + '//' + alias + '.' + this.orgInfo?.portal_url;
	}

	private _userName = '';
	private _userEmail = '';
	private _userId = null;

	get userName() { return this._userName; }
	set userName(value) {
		this._userName = value;
		this.syncChatIdentity();
	}

	get userId() { return this._userId; }
	set userId(value) {
		this._userId = value;
	}

	get userEmail() { return this._userEmail; }
	set userEmail(value) {
		this._userEmail = value;
		this.syncChatIdentity();
	}

	private _routeData: any = {};

	get routeData() { return this._routeData; }
	set routeData(value) {
		this._routeData = value;
	}

	public _routeParams: any = {};

	get routeParams() { return this._routeParams; }
	set routeParams(value) {
		let quoteChange = false;
		let projectChange = false;

		if (value?.quote !== this._routeParams?.quote) quoteChange = true;
		if (value?.project !== this._routeParams?.project) projectChange = true;

		this._routeParams = value;

		if (quoteChange) this.quoteNavigationChange.emit(value?.quote);
		if (projectChange) this.projectNavigationChange.emit(value?.project);
	}

	private _quoteStageChange: EventEmitter<string> = new EventEmitter();
	get quoteStageChange() { return this._quoteStageChange; }

	private _projectStatusChange: EventEmitter<string> = new EventEmitter();
	get projectStatusChange() { return this._projectStatusChange; }

	private _quotePriceChange: EventEmitter<any> = new EventEmitter();
	get quotePriceChange() { return this._quotePriceChange; }

	private _quoteNavigationChange: EventEmitter<any> = new EventEmitter();
	get quoteNavigationChange() { return this._quoteNavigationChange; }

	private _projectNavigationChange: EventEmitter<any> = new EventEmitter();
	get projectNavigationChange() { return this._projectNavigationChange; }

	// Emits the distributor's copy chosen in the advanced search - `product_id` is the product to add. Whoever
	// subscribes also decides whether the modal offers an Add button at all, through its observer count.
	private _globalSearchAdd: EventEmitter<any> = new EventEmitter();
	get globalSearchAdd() { return this._globalSearchAdd; }

	// The answer to the above: `{ product_id, quantity }` for a copy that actually landed on the record. A product
	// with accessories opens the line editor first and can be cancelled there, so the search only marks a row as
	// added once the screen behind it says the line exists - and counts what that line went on for.
	private _globalSearchAdded: EventEmitter<any> = new EventEmitter();
	get globalSearchAdded() { return this._globalSearchAdded; }

	private _originalTitle;

	public onTutorialStepChange = new EventEmitter<number>();

	private _buildVersion = null;
	get buildVersion() { return this._buildVersion; }
	set buildVersion(value) {
		if (value !== this._buildVersion) {
			const oldBuildVersion = this._buildVersion;
			this._buildVersion = value;
			if (oldBuildVersion !== null) this.onBuildVersionChange.emit(this._buildVersion);
		}
	}

	public onBuildVersionChange = new EventEmitter<string>();


	constructor(
		private location: Location,
		private titleService: Title,
		private zone: NgZone,
		private clipboard: Clipboard
	) {
		this._originalTitle = titleService.getTitle();

		// Initialise themes
		const themeList = this.themes;
		$('link[href^="theme-"]').each(function () {
			const linkTitle = $(this).attr('title');
			if (linkTitle && themeList[linkTitle]) {
				themeList[linkTitle].styleSheet = this;
			}
		});

		// Re-apply chat identity once the chat widget signals it's ready (it loads asynchronously,
		// so the identity setters may run before window.WeQuoteChat exists).
		document.addEventListener('wq-chat:ready', () => this.syncChatIdentity());
	}

	// Pushes the current user/company identity into the WeQuote Chat widget — the equivalent of the
	// old Zoho SalesIQ visitor setters. Safe to call repeatedly (last call wins, no re-auth); no-op
	// until the launcher has executed (the wq-chat:ready listener re-syncs once it has).
	private syncChatIdentity() {
		try {
			const chat = window.WeQuoteChat;
			if (!chat) return;

			if (this._userName || this._userEmail || this._org) {
				const identity: any = {};
				if (this._org) identity.accountAlias = this._org;
				if (this.orgInfo?.company_name) identity.companyName = this.orgInfo.company_name;
				if (this._userName) identity.userName = this._userName;
				if (this._userEmail) identity.userEmail = this._userEmail;
				chat.identify(identity);
			} else {
				chat.reset();
			}
		} catch (e) { }
	}

	public ckReady(editor) {
		editor.ui.getEditableElement().parentElement.insertBefore(
			editor.ui.view.toolbar.element,
			editor.ui.getEditableElement()
		);
	}

	redirect(url) {
		window.top.location.href = url;
	}

	canGoBack() {
		return window.history.length > 1;
	}

	back() {
		this.location.back();
	}

	resetSearch() {
		Mangler.each(this.search, k => { this.search[k] = ''; });
	}

	setTitle(title) {
		const newTitle = this._originalTitle + (title ? ' - ' + title : '');
		this.titleService.setTitle(newTitle);
	}

	refreshTitle() {
		let title = this.routeData.title;
		if (title === ':org') title = this.orgInfo ? this.orgInfo.company_name : '';
		this.setTitle(title);
	}

	getLocalFlag(name: string) {
		let data = null;
		try { data = JSON.parse(localStorage.getItem('localFlags')); } catch (ex) { }
		if (!data) return false;
		return !!data[name];
	}

	setLocalFlag(name: string, value: boolean) {
		let data = null;
		try { data = JSON.parse(localStorage.getItem('localFlags')); } catch (ex) { }
		if (!data) data = {};
		data[name] = !!value;
		try { localStorage.setItem('localFlags', JSON.stringify(data)); } catch (ex) { }
	}

	addDefaultLocalOptions(options) {
		// Default options
		return Mangler.merge({
			scheduleView: 'w'
		}, options);
	}

	getLocalOption(name: string) {
		let data = null;
		try { data = JSON.parse(localStorage.getItem('localOptions')); } catch (ex) { }
		if (!data) data = {};
		return this.addDefaultLocalOptions(data)[name];
	}

	setLocalOption(name: string, value: any) {
		let data = null;
		try { data = JSON.parse(localStorage.getItem('localOptions')); } catch (ex) { }
		if (!data) data = {};
		data = this.addDefaultLocalOptions(data);
		data[name] = value;
		try { localStorage.setItem('localOptions', JSON.stringify(data)); } catch (ex) { }
	}

	copyToClipboard(text: string): boolean {
		return this.clipboard.copy(text);
	}

	// Confirms copying something to a marketplace, asking which one when there are several.
	pickMarketplace(what: string, done: (marketplace: any) => void) {
		const list = this.marketplaces;
		if (!list.length) return;

		if (list.length === 1) {
			this.dialog.pop('Copy To Marketplace', 'Are you sure you want to copy ' + what + ' to the marketplace?', ['Cancel', '*Copy To Marketplace'], btn => {
				if (btn === 1) done(list[0]);
			});
		} else {
			// No primary (auto-focused) button, so pressing Enter can't pick a market
			this.dialog.pop('Copy To Marketplace', 'Which marketplace do you want to copy ' + what + ' to?', ['Cancel', ...list.map(m => '+' + m.name)], btn => {
				if (btn > 0) done(list[btn - 1]);
			});
		}
	}

	compareAddress(a, b) {
		if (!a || !b) return false;

		const fields = [
			'address_line_1',
			'address_line_2',
			'address_line_3',
			'posttown',
			'county',
			'postcode',
			'country'
		];

		const optionalFields = ['name', 'description'];

		const norm = (v: any) => ('' + (v ?? '')).trim();

		for (const field of fields) {
			if (
				('' + (a[field] || '')).trim()
				!==
				('' + (b[field] || '')).trim()
			) return false;
		}

		for (const field of optionalFields) {
			const av = norm(a[field]);
			const bv = norm(b[field]);
			if (av && bv && av !== bv) return false;
		}

		return true;
	}

	hasAddress(a, prefix = '') {
		if (!a) return false;
		if (prefix) prefix += '_';

		const fields = [
			'address_line_1',
			'address_line_2',
			'address_line_3',
			'posttown',
			'county',
			'postcode',
			'country'
		];

		for (const field of fields) {
			if (('' + (a[prefix +field] || '')).trim() !== '') return true;
		}

		return false;
	}

	getAddressSingleLine(obj, prefix = '') {
		if (!obj) return '';
		if (prefix) prefix += '_';

		const fields = [
			'address_line_1',
			'address_line_2',
			'address_line_3',
			'posttown',
			'county',
			'postcode',
			'country'
		];

		const addressParts = [];
		fields.forEach(f => { if (obj[prefix + f]) addressParts.push(obj[prefix + f]); });
		return addressParts.filter(part => part).join(', ');
	}

	extractAddressFields(src, prefix) {
		const p = prefix ? (k) => `${prefix}${k}` : (k) => k;
		return {
			name: src?.[p('name')] ?? '',
			description: src?.[p('description')] ?? '',
			address_line_1: src?.[p('address_line_1')] ?? '',
			address_line_2: src?.[p('address_line_2')] ?? '',
			address_line_3: src?.[p('address_line_3')] ?? '',
			posttown: src?.[p('posttown')] ?? '',
			county: src?.[p('county')] ?? '',
			postcode: src?.[p('postcode')] ?? '',
			country: src?.[p('country')] ?? '',
		};
	}

	buildAddressGroups(addresses: any[]) {
		const buckets = {
			organisation: [],
			customer: [],
			customer_contacts: [],
			customer_contact_delivery: [],
			project: [],
			quotes: [],
			other: []
		};

		for (const a of addresses || []) {
			const key = this.groupKeyForAddress(a);
			buckets[key] ??= [];
			buckets[key].push(a);
		}

		const orderedKeys = [
			'project_site',
			'project',
			'warehouse',
			'van',
			'site',
			'customer',
			'customer_contacts',
			'customer_contact_delivery',
			'quotes',
			'organisation',
			'other'
		];

		const out = orderedKeys
			.map((key) => ({
				type: key,
				label: this.groupLabel(key),
				icon: this.groupIcon(key),
				bgColor: this.groupBg(key),
				items: buckets[key],
			}))
			.filter(g => Array.isArray(g.items) && g.items.length > 0);

		return out;
	}

	private groupKeyForAddress(a: any) {
		if (a?.type === 'organisation') {
			return 'organisation';
		}
		if (a?.type === 'customer') {
			if (a?.sub_type === 'contact' && a?.nested_sub_type === 'delivery') {
				return 'customer_contact_delivery';
			}
			return a?.sub_type === 'contact' ? 'customer_contacts' : 'customer';
		}
		if (a?.type === 'project') return 'project';

		if (a?.type === 'quote') return 'quotes';
		if (a?.type === 'warehouse') return 'warehouse';
		if (a?.type === 'van') return 'van';
		if (a?.type === 'project_site') return 'project_site';
		if (a?.type === 'site') return 'site';

		return 'other';
	}

	private groupLabel(key) {
		switch (key) {
			case 'quotes': return 'Quotes';
			case 'project': return 'Project';
			case 'customer': return 'Customer';
			case 'customer_contacts': return 'Customer Contacts';
			case 'customer_contact_delivery': return 'Contact Delivery';
			case 'organisation': return 'Organisation';
			case 'warehouse': return 'Warehouse Location';
			case 'van': return 'Van';
			case 'project_site': return 'Site Location';
			case 'site': return 'Custom Location';
			default: return 'Other';
		}
	}

	private groupIcon(key) {
		switch (key) {
			case 'quotes': return 'wq wq-handshake';
			case 'project': return 'wq wq-booking-project';
			case 'customer': return 'wq wq-customer';
			case 'customer_contacts': return 'wq wq-user';
			case 'customer_contact_delivery': return 'wq wq-truck';
			case 'organisation': return 'wq wq-client';
			case 'warehouse': return 'wq wq-warehouse';
			case 'van': return 'wq wq-truck';
			case 'project_site': return 'wq wq-booking-project';
			case 'site': return 'wq wq-client';
			default: return 'wq wq-location';
		}
	}

	private groupBg(key) {
		switch (key) {
			case 'quotes': return 'danger';
			case 'project': return 'primary';
			case 'customer': return 'warning';
			case 'customer_contacts': return 'info';
			case 'customer_contact_delivery': return 'warning';
			case 'organisation': return 'success';
			case 'warehouse': return 'secondary';
			case 'van': return 'secondary';
			case 'project_site': return 'primary';
			case 'site': return 'info';
			default: return 'secondary';
		}
	}

	getIconClasses() {
		const result = [];

		for (let i = 0; i < document.styleSheets.length; i++) {
			const sheet = document.styleSheets[i];
			if (sheet.href && sheet.href.includes('/wequote-icons.css')) {
				for (let j = 0; j < sheet.cssRules.length; j++) {
					const rule = sheet.cssRules[j];
					if (rule instanceof CSSStyleRule && rule.selectorText) {
						const match = rule.selectorText.match(/\.(wq-.*)::/);
						if (match) result.push({
							id: match[1],
							name: match[1].replace(/^wq-/, '').replace(/-/g, ' ').replace(/(^[a-z]| [a-z]|-[a-z])/g, s => s.toUpperCase())
						});
					}
				}
			}
		}

		return result;
	}

	switchTheme(themeName) {
		if (!themeName) return;

		const theme = this.themes[themeName];
		if (!theme?.styleSheet || this.activeTheme === theme) return;

		// Find and enable new theme
		this.activeTheme = theme;
		$(theme.styleSheet).prop('disabled', false);

		// If found, disable the other themes
		// Using setTimeout will reduce flickering
		setTimeout(() => {
			Mangler.each(this.themes, (k, v) => {
				if (v !== theme) $(v.styleSheet).prop('disabled', true);
			});
		}, 0);
	}

	trackById(_index, value) { return value?.id; }
	trackByIndex(index, _value) { return index; }

	countryISO3toISO2(iso3) {
		const country = countryLookup.byIso(iso3);
		if (country) return country.iso2.toLowerCase();
		return '';
	}

}
